import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { HeartPulse, BellRing, CreditCard, Sparkles, Check } from 'lucide-react';
import { findCloudPlan } from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert, TextField } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { useSession } from '@/lib/session';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useAsync } from '@/lib/useAsync';

/** Only same-site paths are honoured as a post-login destination. */
function safeNext(raw: string | null, fallback: string): string {
  return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : fallback;
}

function AuthShell({ title, subtitle, children }: { title: string; subtitle: ReactNode; children: ReactNode }) {
  return (
    <div className="min-h-[calc(100vh-110px)] grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between bg-navy-900 text-white p-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark" />
        <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-brand-primary/25 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-secondary/25 blur-[120px]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-accent">Scada.Ink Customer Portal</div>
          <h2 className="mt-4 text-3xl font-bold leading-tight max-w-md">Your appliances, cloud services and alerts in one place.</h2>
        </div>
        <ul className="relative space-y-5 max-w-md">
          {[
            { icon: HeartPulse, title: 'Fleet health', body: 'See every connected appliance’s status and warnings.' },
            { icon: CreditCard, title: 'Subscriptions', body: 'Subscribe to AI, archive, remote viewing and alerts.' },
            { icon: BellRing, title: 'SMS & voice', body: 'Manage Twilio settings, recipients and caps.' },
            { icon: Sparkles, title: 'AI usage', body: 'Track Smart Assist usage against your plan.' },
          ].map((f) => (
            <li key={f.title} className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <f.icon size={18} className="text-brand-accent" />
              </span>
              <span>
                <span className="block font-semibold">{f.title}</span>
                <span className="block text-sm text-slate-400">{f.body}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-center px-4 py-14">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <div className="mt-2 text-text-secondary">{subtitle}</div>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  usePageTitle('Log in');
  const { session, loading, login, verify } = useSession();
  useEffect(verify, [verify]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = safeNext(params.get('next'), '/portal');

  if (!loading && session) return <Navigate to={next} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ email, password });
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Log in"
      subtitle={
        <>
          New to Scada.Ink Cloud?{' '}
          <Link to={`/register${params.toString() ? `?${params}` : ''}`} className="font-semibold text-brand-primary">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <TextField label="Email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {error && <Alert kind="error">{error}</Alert>}
        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Log in'}
        </Button>
        <p className="text-xs text-text-muted text-center">
          <Link to="/forgot-password" className="text-brand-primary font-semibold">Forgot your password?</Link>
        </p>
      </form>
      <p className="mt-10 text-xs text-text-muted text-center">
        This is the Scada.Ink Cloud portal. To sign in to an appliance, open its address on your plant network.
      </p>
    </AuthShell>
  );
}

export function RegisterPage() {
  usePageTitle('Create account');
  const { session, loading, register, verify } = useSession();
  useEffect(verify, [verify]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const plan = findCloudPlan(params.get('plan') ?? '');
  const next = plan ? `/portal/billing?plan=${plan.id}` : safeNext(params.get('next'), '/portal');

  if (!loading && session) return <Navigate to={next} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (form.password.length < 10) {
      setError('Use a password of at least 10 characters.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await register(form);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle={
        <>
          Already have one?{' '}
          <Link to={`/login${params.toString() ? `?${params}` : ''}`} className="font-semibold text-brand-primary">
            Log in
          </Link>
        </>
      }
    >
      {plan && (
        <div className="mb-6 flex items-center gap-3 rounded-[var(--radius-glass-sm)] border border-brand-primary/25 bg-brand-primary/5 px-4 py-3">
          <Check size={16} className="text-brand-primary" />
          <span className="text-sm">
            Next: subscribe to <b>{plan.name}</b> — ${plan.priceMonthlyUsd}/{plan.unit === 'appliance' ? 'appliance/' : ''}month
          </span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <TextField label="Full name" required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <TextField label="Work email" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <TextField label="Company" required autoComplete="organization" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        <TextField
          label="Password"
          type="password"
          required
          minLength={10}
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          hint="At least 10 characters."
        />
        {error && <Alert kind="error">{error}</Alert>}
        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
        <p className="text-xs text-text-muted text-center">Fleet health monitoring is free. Paid services are only billed when you subscribe.</p>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  usePageTitle('Reset password');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await cloudApi.post('/api/portal/auth/forgot', { email });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Reset your password" subtitle="Enter the email you sign in with, on the portal or in the Scada.Ink app.">
      {sent ? (
        <Alert kind="success">
          If {email} has a Scada.Ink sign-in, a reset link is on its way. It works once and expires in 30 minutes. Check your spam folder if it doesn't arrive.
        </Alert>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <TextField label="Email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          {error && <Alert kind="error">{error}</Alert>}
          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
      <p className="mt-6 text-xs text-text-muted text-center">
        <Link to="/login" className="text-brand-primary font-semibold">Back to log in</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  usePageTitle('Choose a new password');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useSession();
  const check = useAsync(() => cloudApi.get<{ email: string }>(`/api/portal/auth/reset?token=${encodeURIComponent(token)}`), [token]);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 10) return setError('Use a password of at least 10 characters.');
    setBusy(true);
    setError(null);
    try {
      await cloudApi.post('/api/portal/auth/reset', { token, password });
      await refresh();
      navigate('/portal', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Setting it signs you out of every browser and phone.">
      {check.loading && !check.data ? (
        <p className="text-sm text-text-muted">Checking your link…</p>
      ) : check.error || !check.data ? (
        <>
          <Alert kind="error">{check.error ?? 'This reset link cannot be used.'}</Alert>
          <p className="mt-6 text-sm text-center">
            <Link to="/forgot-password" className="text-brand-primary font-semibold">Ask for a new link</Link>
          </p>
        </>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <TextField label="Email" value={check.data.email} disabled />
          <TextField label="New password" type="password" required minLength={10} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} hint="At least 10 characters." />
          {error && <Alert kind="error">{error}</Alert>}
          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {busy ? 'Saving…' : 'Set password and sign in'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
